import { ICountry_Supplier } from '@bridebook/models/source/models/Countries.types';
import type { IBucket } from '@bridebook/models/source/models/Countries/Buckets.types';
import { ISupplier_Type } from '@bridebook/models/source/models/Suppliers.types';
import { PremiumTiers } from '@bridebook/toolbox/src/constants';
import type { GetSettingReturnType, Market } from '@bridebook/toolbox/src/gazetteer';

export type SupplierBucketSettingResult<K extends keyof IBucket['settings']> =
  | {
      isEnabled: true;
      isUnlocked: boolean;
      minimumTierRequired: number;
      setting: NonNullable<GetSettingReturnType<K, ICountry_Supplier>>;
    }
  | {
      isEnabled: false;
      isUnlocked: boolean;
      minimumTierRequired: number;
      setting: null;
    };

function hasTierWithMinimumByType(setting: unknown): boolean {
  return (
    setting !== null &&
    setting !== false &&
    typeof setting === 'object' &&
    'tier' in setting &&
    setting.tier !== null &&
    typeof setting.tier === 'object' &&
    'minimumByType' in setting.tier &&
    setting.tier.minimumByType !== undefined
  );
}

/**
 * Resolve a bucket setting for a supplier, supporting both type-filtered and non-type-filtered
 * settings. Returns the setting value, whether it's enabled, unlocked, and the minimum tier
 * required.
 *
 * @param settingName The setting key to retrieve.
 * @param type The supplier type (required for type-filtered settings).
 * @param market The market instance.
 * @param tier The supplier's premium tier.
 * @returns An object with isEnabled, isUnlocked, minimumTierRequired, and setting (null if not enabled).
 */
export function resolveSupplierBucketSetting<K extends keyof IBucket['settings']>(
  settingName: K,
  type: ISupplier_Type | undefined,
  market?: Market,
  tier: PremiumTiers | undefined = PremiumTiers.Unverified,
): SupplierBucketSettingResult<K> {
  if (!type || !market) {
    return {
      isEnabled: false,
      isUnlocked: false,
      minimumTierRequired: -Infinity,
      setting: null,
    };
  }

  let setting: GetSettingReturnType<K, ICountry_Supplier> | IBucket['settings'][K] | null | boolean;

  try {
    setting = market.getSetting(settingName);
  } catch {
    // getSetting throws for type-based settings, so resolve against current supplier type.
    setting = market.getSettingForType(settingName, type);
  }

  const isEnabled = setting !== null && setting !== false;
  const shouldCheckByType = hasTierWithMinimumByType(setting);
  const minimumTierRequired = market.getSettingRequiredTier(
    settingName,
    shouldCheckByType ? type : undefined,
  );
  const isUnlocked = isEnabled && tier >= minimumTierRequired;

  if (isEnabled) {
    return {
      isEnabled: true,
      isUnlocked,
      minimumTierRequired,
      setting: setting as NonNullable<GetSettingReturnType<K, ICountry_Supplier>>,
    };
  }

  return {
    isEnabled: false,
    isUnlocked: false,
    minimumTierRequired,
    setting: null,
  };
}
